import { useMemo, useEffect, useState } from 'react';
import { Inbox, ArrowRight } from 'lucide-react';
import { useTasks } from '../hooks/useTasks';
import { useTimeBlocks } from '../hooks/useTimeBlocks';
import { useGoogleCalendar, TIME_BLOCK_NAMES } from '../hooks/useGoogleCalendar';
import { DashboardHabitsColumn, DashboardTasksColumn, DashboardScheduleColumn } from './dashboard';
import { DragProvider } from '../contexts/DragContext';
import { getTodayString } from '../utils/dateUtils';

export default function CockpitView({
  habits = [],
  isChecked,
  toggleCheck,
  getCheckoffFrequency,
  onNavigateToInbox
}) {
  const { tasks, completeTask, uncompleteTask, assignToBlock, getInboxTasks } = useTasks();

  // Get inbox count for the Inbox card
  const inboxTasks = useMemo(() => getInboxTasks(), [getInboxTasks]);
  const inboxCount = inboxTasks.length;
  const { timeBlocks } = useTimeBlocks();
  const { getAllEventsForDate, isSignedIn } = useGoogleCalendar();

  // Local state for calendar events
  const [calendarEvents, setCalendarEvents] = useState([]);

  // Get today's date string (using local timezone, not UTC)
  const today = useMemo(() => getTodayString(), []);

  // Fetch all calendar events for today on mount
  useEffect(() => {
    const fetchTodayEvents = async () => {
      if (isSignedIn && getAllEventsForDate) {
        const events = await getAllEventsForDate(today);
        setCalendarEvents(events || []);
      }
    };
    fetchTodayEvents();
  }, [isSignedIn, getAllEventsForDate, today]);

  // Get today's tasks (focused for today, scheduled for today, OR overdue)
  // Also includes tasks completed today (for the Completed section)
  const todayTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (t.deleted) return false;
      // Tasks with focusedForToday flag (including completed ones for "completed today")
      if (t.focusedForToday) return true;
      // Tasks scheduled for today
      if (t.scheduledDate === today) return true;
      // Overdue tasks (scheduled in the past, not completed)
      if (!t.completedAt && t.scheduledDate && t.scheduledDate < today) return true;
      return false;
    });
  }, [tasks, today]);

  // Sort today's events by start time
  const todayEvents = useMemo(() => {
    return [...calendarEvents].sort((a, b) => {
      const aStart = new Date(a.start?.dateTime || a.start?.date);
      const bStart = new Date(b.start?.dateTime || b.start?.date);
      return aStart - bStart;
    });
  }, [calendarEvents]);

  // Get today's time blocks (subset of events that match TIME_BLOCK_NAMES)
  const todayBlocks = useMemo(() => {
    return todayEvents.filter((event) => {
      const summary = event.summary?.toLowerCase() || '';
      return TIME_BLOCK_NAMES.some(name => summary.includes(name.toLowerCase()));
    });
  }, [todayEvents]);

  // Fallback to legacy timeBlocks if no Google Calendar events
  const effectiveTimeBlocks = useMemo(() => {
    if (todayBlocks.length > 0) return todayBlocks;

    if (!timeBlocks) return [];
    return timeBlocks
      .filter((b) => b.start && b.start.startsWith(today))
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [todayBlocks, timeBlocks, today]);

  // Sort today's tasks by time block position
  const sortedTodayTasks = useMemo(() => {
    return [...todayTasks].sort((a, b) => {
      const blockA = effectiveTimeBlocks.find(tb => tb.id === a.gcalTimeBlockId);
      const blockB = effectiveTimeBlocks.find(tb => tb.id === b.gcalTimeBlockId);

      if (blockA && blockB) {
        const aStart = new Date(blockA.start?.dateTime || blockA.start);
        const bStart = new Date(blockB.start?.dateTime || blockB.start);
        return aStart - bStart;
      }
      if (blockA) return -1;
      if (blockB) return 1;
      return 0;
    });
  }, [todayTasks, effectiveTimeBlocks]);

  // Get tasks for a specific block
  const getTasksForBlock = (blockId) => {
    return tasks.filter(t => t.gcalTimeBlockId === blockId);
  };

  // Handle dropping a task on a timeblock
  const handleDropOnBlock = async (taskId, block) => {
    const scheduledDate = block.start?.dateTime?.split('T')[0] || block.start?.date || today;
    await assignToBlock(taskId, block.id, block.summary, scheduledDate);
  };

  return (
    <DragProvider>
      {/* Inbox Card - Shows when there are items in inbox */}
      {inboxCount > 0 && onNavigateToInbox && (
        <div className="mb-4">
          <button
            onClick={onNavigateToInbox}
            className="w-full p-4 bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-xl hover:border-accent/40 hover:from-accent/15 hover:to-accent/10 transition-all group cursor-pointer text-left"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent/20 rounded-lg">
                  <Inbox size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-primary">
                    {inboxCount} item{inboxCount !== 1 ? 's' : ''} in Inbox
                  </p>
                  <p className="text-xs text-text-tertiary">
                    Process your captured items to stay organized
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-accent text-sm font-medium opacity-70 group-hover:opacity-100 transition-opacity">
                <span>Process Now</span>
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </button>
        </div>
      )}

      <div className="dashboard-grid">
        {/* Habits Column */}
        <DashboardHabitsColumn
          habits={habits}
          isChecked={isChecked}
          toggleCheck={toggleCheck}
          getCheckoffFrequency={getCheckoffFrequency}
        />

        {/* Tasks Column */}
        <DashboardTasksColumn
          tasks={sortedTodayTasks}
          timeBlocks={effectiveTimeBlocks}
          onComplete={completeTask}
          onUncomplete={uncompleteTask}
        />

        {/* Schedule Column */}
        <DashboardScheduleColumn
          events={todayEvents}
          timeBlocks={effectiveTimeBlocks}
          tasks={tasks}
          getTasksForBlock={getTasksForBlock}
          onDropTask={handleDropOnBlock}
        />
      </div>
    </DragProvider>
  );
}
